import {
  ADMIN_URL,
  BACKEND_URL,
  PORTAL_TOKEN_NAME,
  REFRESH_TOKEN,
} from '../constants/apiConstants'
import {
  ideationDraftPath,
  ideationPublicPath,
  userConversationsPath,
} from '../constants/pathConstants'
import {getCookies, removeCookies} from './cookiesUtil'
import {urlUtil} from './urlUtil'

/**
 * These three routes (declared outside the authenticated tree in
 * src/routes/index.tsx) exist specifically so an anonymous visitor can open them —
 * a shared conversation link and a published/draft ideation preview. They are served
 * from the same host as the admin SPA, so hostname alone cannot tell them apart from
 * an authenticated admin screen; the path has to.
 */
const isPublicRoutePath = (pathname: string): boolean =>
  pathname.startsWith(userConversationsPath) ||
  pathname.startsWith(ideationPublicPath) ||
  pathname.startsWith(ideationDraftPath)

export const getIsAdminUI = (): boolean => {
  if (isPublicRoutePath(window.location.pathname)) return false

  const host = window.location.host.split('.')[0]
  return (
    host === 'empower' ||
    host === 'empower-staging' ||
    // Local TLS host (https://empower-local.questionpro.com). It has to be a
    // questionpro.com name so it is same-site with the API (dev-empower.questionpro.com);
    // otherwise Firefox and Safari treat the admin session cookie as third-party and
    // never send it. Mapped to 127.0.0.1 in /etc/hosts — *.questionpro.com is a public
    // wildcard, so a missing line reaches the internet instead of failing locally.
    host === 'empower-local' ||
    host === 'localhost:3000'
  )
}

/**
 * Fails loudly when `getIsAdminUI()` disagrees with the deployed environment.
 *
 * The hostname prefix alone decides which route tree every request goes to
 * (`/admin/*` vs the portal paths) and which credential is sent. A wrong answer is not
 * a subtle bug — every call 404s or 401s — but it is invisible in the source, so the
 * deploy config is checked against it once at startup instead.
 *
 * Skipped on localhost, where the dev server may run on any port, and skipped entirely
 * when `REACT_APP_ADMIN_URL` is unset, since there is then nothing to compare against.
 * Also skipped on the three public route paths `getIsAdminUI()` exempts — those
 * legitimately answer `false` regardless of host, so comparing them against the
 * deployed admin origin would flag a real public-page load as a misconfiguration.
 */
export const assertAdminUiHost = (): void => {
  if (!ADMIN_URL) return

  const {origin, hostname, pathname} = window.location
  if (hostname === 'localhost' || hostname.endsWith('.localhost')) return
  if (isPublicRoutePath(pathname)) return

  const isAdminOrigin = origin === ADMIN_URL.replace(/\/+$/, '')
  if (getIsAdminUI() === isAdminOrigin) return

  const message =
    `Admin-surface detection disagrees with the deployed config: getIsAdminUI() is ` +
    `${getIsAdminUI()} for origin ${origin}, but REACT_APP_ADMIN_URL is ${ADMIN_URL}. ` +
    `Requests would go to the wrong route tree. Fix REACT_APP_ADMIN_URL for this ` +
    `environment, or the hostname list in getIsAdminUI().`

  if (process.env.NODE_ENV !== 'production') throw new Error(message)
  console.error(message)
}

/** Sentinel for the admin surface, whose real credential the client cannot read. */
export const ADMIN_SESSION_CREDENTIAL = 'session'

/**
 * The value the `enabled` gates key off before firing an authenticated request.
 *
 * Portal reads its bearer token from a JS-readable cookie, so absence of the cookie
 * genuinely means "not logged in". Admin authenticates with the backend's httpOnly
 * session cookie, which the client cannot inspect by design — so there is nothing to
 * check and the server is the only authority. Returning a constant lets admin requests
 * fire and leaves the "no session" case to the 401 interceptor, which starts a fresh
 * OAuth round trip.
 */
export const getAuthCredential = (): string | undefined =>
  getIsAdminUI() ? ADMIN_SESSION_CREDENTIAL : getCookies(PORTAL_TOKEN_NAME)

const toSecureOrigin = (url: string): string => {
  const u = new URL(url)
  u.port = ''
  u.protocol = 'https:'
  return u.origin
}

/** Per-tab memory of the `appOrigin` the operator arrived with. See `resolveAppOrigin()`. */
const APP_ORIGIN_KEY = 'empowerAppOrigin'

/**
 * `appOrigin` for this tab — from the current URL, else the one this tab arrived with.
 *
 * Only the entry URL core admin hands us carries `appOrigin`; a client-side navigation
 * drops the query string. Without this, the first 401 authorizes against the right host
 * but a later one — an expired session three screens in — falls back to
 * `QUESTIONPRO_OAUTH_HOST_URL` and drops the operator on a login form mid-session, which
 * is the same failure this fix exists to remove, just deferred.
 *
 * Scoped to `sessionStorage`, so it lives exactly as long as the tab the operator
 * switched into and never leaks into another tab or a later session. Best-effort: a
 * blocked or full store throws, and the entry-URL value still works on its own.
 */
const resolveAppOrigin = (searchParams: URLSearchParams): string | null => {
  const fromUrl = searchParams.get('appOrigin')

  try {
    if (fromUrl) {
      sessionStorage.setItem(APP_ORIGIN_KEY, fromUrl)
      return fromUrl
    }
    return sessionStorage.getItem(APP_ORIGIN_KEY)
  } catch {
    return fromUrl
  }
}

/**
 * Where to send a caller who has no valid credential.
 *
 * Admin hands the whole exchange to the backend: `GET /oauth` builds the QuestionPro
 * authorize URL from server-side config and redirects, and `GET /oauth/callback`
 * completes it and bounces back into the SPA. Nothing about the OAuth client is known
 * to the browser any more — no client id, no redirect_uri, no code handling.
 *
 * `state` round-trips through QuestionPro and comes back to the backend's
 * `getRedirectUri`, which reads `subPath` and `params` off it to rebuild the page the
 * caller was on. Only the path and query travel; the host always comes from backend
 * config, so a tampered state cannot redirect anywhere else.
 */
export const getLoginUrl = (): string => {
  const {origin, host, pathname, search} = window.location

  if (getIsAdminUI()) {
    const searchParams = new URLSearchParams(search)
    const params = Object.fromEntries(searchParams)
    const state = JSON.stringify({
      subPath: pathname.replace(/^\/+/, ''),
      ...(Object.keys(params).length > 0 && {params}),
    })

    /**
     * `appOrigin` names the QuestionPro data centre to authorize against, and has to
     * travel as its own query param — the copy inside `state` is opaque to the package
     * and only feeds `getRedirectUri`'s landing-URL rebuild.
     *
     * It is what makes the core-admin product switch work. Core admin hands us the host
     * the operator is already signed in on (e.g. `https://aeadmin.questionpro.com`), and
     * that host's session cookies are host-only, so authorizing anywhere else arrives
     * anonymous and drops the operator on a login form. Forwarding it authorizes against
     * that same host, which does receive its own cookies, and SSO completes silently.
     *
     * Dropping it is safe by construction: the package validates the value against its
     * own QuestionPro host regex and falls back to `QUESTIONPRO_OAUTH_HOST_URL` when it
     * is missing or fails, so a bad value cannot redirect the operator off-domain.
     *
     * Resolved through `resolveAppOrigin()` rather than read straight off the URL, so a
     * 401 raised after the SPA has navigated away from the entry URL still authorizes
     * against the host the operator actually arrived from.
     */
    const appOrigin = resolveAppOrigin(searchParams)
    const query = [
      ...(appOrigin ? [`appOrigin=${encodeURIComponent(appOrigin)}`] : []),
      `state=${encodeURIComponent(state)}`,
    ].join('&')

    return `${urlUtil.joinUrl(BACKEND_URL ?? '', 'oauth')}?${query}`
  }

  let qpHost: string
  if (origin.includes('.empower-portal-staging.questionpro')) {
    qpHost = toSecureOrigin(origin.replace('.empower-portal-staging', ''))
  } else if (origin.includes('.localhost')) {
    qpHost = toSecureOrigin(`https://${host.split('.')[0]}.questionpro.com`)
  } else {
    qpHost = toSecureOrigin(origin.replace('.empower', ''))
  }

  const portalOrigin = localStorage.getItem('wfPortalOrigin')

  if (portalOrigin) {
    const v1State = encodeURIComponent(
      JSON.stringify({migrationVersion: 1, portalOrigin}),
    )
    const urlObject = new URL(`${portalOrigin}/portal/redirect/empower`)
    urlObject.search = `redirectUrl=${origin}/authorization/callback&clientId=${process.env.REACT_APP_PORTAL_CLIENT_ID}&state=${v1State}`
    return urlObject.toString()
  }

  const urlObject = new URL(`${qpHost}/w/redirect/empower`)
  urlObject.search = `redirectUrl=${origin}/authorization/callback&clientId=${process.env.NEXT_PUBLIC_CLIENT_ID}&state=${pathname}${search}`
  return urlObject.toString()
}

export const logout = (): void => {
  if (getIsAdminUI()) {
    // The session lives on the server, so only the server can end it. `/oauth/logout`
    // destroys it, clears the session cookie and forwards to QuestionPro's own logout
    // so the SSO session goes with it. Dropping a cookie here would leave both alive.
    window.location.href = urlUtil.joinUrl(BACKEND_URL ?? '', 'oauth/logout')
    return
  }

  removeCookies(PORTAL_TOKEN_NAME)
  removeCookies(REFRESH_TOKEN)

  const portalOrigin = localStorage.getItem('wfPortalOrigin')
  if (portalOrigin) {
    localStorage.removeItem('wfPortalOrigin')
    window.location.href = `${portalOrigin}/portal/logout`
  } else {
    const origin = window.location.origin.split('.')[0]
    window.location.href = `${origin}.questionpro.com/w/logout`
  }
}

export const getWorkforceUrl = (): string => {
  if (getIsAdminUI()) return ''
  const portalOrigin = localStorage.getItem('wfPortalOrigin')
  if (portalOrigin) return `${portalOrigin}/portal/`
  const host = window.location.host.split('.')[0]
  return `https://${host}.questionpro.com/w/dashboard`
}

export const authUtil = {
  getIsAdminUI,
  assertAdminUiHost,
  getAuthCredential,
  getLoginUrl,
  logout,
  getWorkforceUrl,
} as const
